import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Alert } from '../components/ui/Alert';
import { Sheet, SheetContent, SheetBody, SheetFooter } from '../components/ui/Sheet';
import { MockBadge } from '../components/MockBadge';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/format';
import { TOTP_LOCKED_MESSAGE, isTotpLocked } from '../lib/authErrors';
import {
  STEP_UP_ENDED_MESSAGE,
  isStepUpRequired,
  stepUpBody,
  stepUpCodeReady,
  useStepUpWindow,
} from '../lib/stepUp';
import { StepUpCodeField } from '../components/StepUpCodeField';
import {
  ApiError,
  type WooOrderSignupStatus,
  type WooSettingsStatus,
  type WooWebhookStatus,
} from '../types/api';

function setKeyErrorText(err: unknown): string {
  if (isTotpLocked(err)) return TOTP_LOCKED_MESSAGE;
  if (isStepUpRequired(err)) return STEP_UP_ENDED_MESSAGE;
  if (err instanceof ApiError && err.status === 401)
    return 'The code is incorrect or has already been used.';
  if (err instanceof ApiError && err.status === 400 && err.body.message) return err.body.message;
  return 'The key could not be saved. Try again.';
}

function testConnectionText(err: unknown): string {
  if (err instanceof ApiError && err.status === 409) return 'No key has been saved yet.';
  if (err instanceof ApiError && err.status === 502)
    return 'The connection to the shop failed. Check the URL, key and secret.';
  return 'The connection could not be tested. Try again.';
}

function SetKeySheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [pluginUrl, setPluginUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const queryClient = useQueryClient();
  const stepUp = useStepUpWindow();
  const pluginUrlId = useId();
  const secretId = useId();
  const codeId = useId();

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch<WooSettingsStatus>('/settings/woo', {
        method: 'POST',
        body: {
          pluginUrl: pluginUrl.trim(),
          secret: secret.trim(),
          ...(stepUp.active ? {} : stepUpBody(totpCode)),
        },
      }),
    onError: stepUp.onStepUpError,
    onSuccess: () => {
      if (!stepUp.active) stepUp.onStepUpSuccess();
      onOpenChange(false);
      setPluginUrl('');
      setSecret('');
      setTotpCode('');
      void queryClient.invalidateQueries({ queryKey: ['woo-settings'] });
    },
  });

  const canSubmit =
    pluginUrl.trim() !== '' &&
    secret.trim() !== '' &&
    (stepUp.active || stepUpCodeReady(totpCode)) &&
    !mutation.isPending;

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) mutation.reset();
      }}
    >
      {open ? (
        <SheetContent
          eyebrow="WooCommerce"
          title="Set or replace plugin key"
          description="The key is used to call the WooCommerce plugin in the shop (coupons and order history). Requires a code from the authenticator app (not if you entered one in the last 30 minutes)."
        >
          <form
            id="woo-set-key-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) mutation.mutate();
            }}
          >
            <SheetBody>
              {mutation.isError ? <Alert>{setKeyErrorText(mutation.error)}</Alert> : null}
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={pluginUrlId}>Plugin URL</Label>
                <Input
                  id={pluginUrlId}
                  type="url"
                  placeholder="https://shop.example.com"
                  value={pluginUrl}
                  onChange={(e) => setPluginUrl(e.target.value)}
                  required
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={secretId}>Shared secret</Label>
                <Input
                  id={secretId}
                  type="password"
                  autoComplete="new-password"
                  placeholder="at least 16 characters"
                  className="font-mono text-sm"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  required
                />
              </div>
              <StepUpCodeField
                id={codeId}
                value={totpCode}
                onChange={setTotpCode}
                active={stepUp.active}
                minutesLeft={stepUp.minutesLeft}
              />
            </SheetBody>
            <SheetFooter>
              <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={!canSubmit}>
                {mutation.isPending ? 'Saving…' : 'Save key'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      ) : null}
    </Sheet>
  );
}

/**
 * NP-Flow Woo plugin key (`tenant_keys`, purpose `woo_plugin_secret`) —
 * used for the coupon step in automations and order history backfill. No
 * WooCommerce REST key anymore (one plugin instead of several, 28/9).
 * Status only shows configured/not + the saved plugin URL, never the shared
 * secret (`apps/app/src/routes/wooSettings.ts`). Set/replace requires a
 * fresh TOTP code (same pattern as resuming a sender).
 */
export function WooSection() {
  const { isAdmin } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['woo-settings'],
    queryFn: () => apiFetch<WooSettingsStatus>('/settings/woo'),
  });

  const testMutation = useMutation({
    mutationFn: () => apiFetch('/settings/woo/test-connection', { method: 'POST', body: {} }),
  });

  if (!isAdmin) {
    return (
      <Card className="p-4 flex flex-col gap-2">
        <h2 className="text-sm font-semibold m-0">WooCommerce</h2>
        <Alert>You have read-only access. Contact an administrator to set the plugin key.</Alert>
      </Card>
    );
  }

  return (
    <Card className="p-4 flex flex-col gap-3 max-w-[640px]">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold m-0">WooCommerce</h2>
        <MockBadge />
      </div>
      <p className="m-0 text-sm text-muted">
        The plugin key is used for the coupon step in automations and for order history backfill.
        The shared secret is never shown again once it is saved.
      </p>

      {isLoading ? <div className="text-muted text-sm">Loading status…</div> : null}
      {isError ? <Alert>Status could not be loaded.</Alert> : null}

      {data ? (
        <div className="flex flex-col gap-1 text-sm">
          <div>
            Status:{' '}
            <span className={data.configured ? 'text-success-text' : 'text-muted'}>
              {data.configured ? 'Configured' : 'Not configured'}
            </span>
          </div>
          {data.configured ? (
            <>
              <div>
                Plugin URL: <span className="font-mono">{data.pluginUrl}</span>
              </div>
              {data.lastRotatedAt !== null ? (
                <div className="text-xs text-muted">
                  Last set {formatDateTime(data.lastRotatedAt)}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      {testMutation.isError ? <Alert>{testConnectionText(testMutation.error)}</Alert> : null}
      {testMutation.isSuccess ? (
        <div role="status" className="text-success-text text-sm">
          The connection works.
        </div>
      ) : null}

      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="secondary" onClick={() => setSheetOpen(true)}>
          {data?.configured ? 'Replace key' : 'Set key'}
        </Button>
        {data?.configured ? (
          <Button
            variant="secondary"
            disabled={testMutation.isPending}
            onClick={() => testMutation.mutate()}
          >
            {testMutation.isPending ? 'Testing…' : 'Test connection'}
          </Button>
        ) : null}
      </div>

      <SetKeySheet
        open={sheetOpen}
        onOpenChange={(next) => {
          setSheetOpen(next);
          if (!next) void queryClient.invalidateQueries({ queryKey: ['woo-settings'] });
        }}
      />
    </Card>
  );
}

function setWebhookSecretErrorText(err: unknown): string {
  if (isTotpLocked(err)) return TOTP_LOCKED_MESSAGE;
  if (isStepUpRequired(err)) return STEP_UP_ENDED_MESSAGE;
  if (err instanceof ApiError && err.status === 401)
    return 'The code is incorrect or has already been used.';
  if (err instanceof ApiError && err.status === 400 && err.body.message) return err.body.message;
  return 'The secret could not be saved. Try again.';
}

function SetWebhookSecretSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [secret, setSecret] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const queryClient = useQueryClient();
  const stepUp = useStepUpWindow();
  const secretId = useId();
  const codeId = useId();

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch<WooWebhookStatus>('/settings/woo/webhook', {
        method: 'POST',
        body: { secret: secret.trim(), ...(stepUp.active ? {} : stepUpBody(totpCode)) },
      }),
    onError: stepUp.onStepUpError,
    onSuccess: () => {
      if (!stepUp.active) stepUp.onStepUpSuccess();
      onOpenChange(false);
      setSecret('');
      setTotpCode('');
      void queryClient.invalidateQueries({ queryKey: ['woo-webhook'] });
    },
  });

  const canSubmit =
    secret.trim() !== '' && (stepUp.active || stepUpCodeReady(totpCode)) && !mutation.isPending;

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) mutation.reset();
      }}
    >
      {open ? (
        <SheetContent
          eyebrow="WooCommerce"
          title="Set or replace webhook secret"
          description="Use the same secret as in the shop's webhook. The previous secret keeps working until the next replacement, so no orders are lost while you update the shop. Requires a code from the authenticator app (not if you entered one in the last 30 minutes)."
        >
          <form
            id="woo-set-webhook-secret-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) mutation.mutate();
            }}
          >
            <SheetBody>
              {mutation.isError ? <Alert>{setWebhookSecretErrorText(mutation.error)}</Alert> : null}
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={secretId}>Webhook secret</Label>
                <Input
                  id={secretId}
                  type="password"
                  autoComplete="new-password"
                  placeholder="16 to 200 characters"
                  className="font-mono text-sm"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  required
                />
              </div>
              <StepUpCodeField
                id={codeId}
                value={totpCode}
                onChange={setTotpCode}
                active={stepUp.active}
                minutesLeft={stepUp.minutesLeft}
              />
            </SheetBody>
            <SheetFooter>
              <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={!canSubmit}>
                {mutation.isPending ? 'Saving…' : 'Save secret'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      ) : null}
    </Sheet>
  );
}

/**
 * Order webhook (`tenant_keys`, purpose `wc_webhook_secret`): the shop sends
 * every order to NP-Flow, signed with this secret
 * (`apps/edge/src/routes/wooWebhook.ts`). Shows the delivery URL to type
 * into WooCommerce, never the secret. Admin only; the read-only notice for
 * other roles lives in `WooSection`.
 */
export function WooWebhookSection() {
  const { isAdmin } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['woo-webhook'],
    queryFn: () => apiFetch<WooWebhookStatus>('/settings/woo/webhook'),
    enabled: isAdmin,
  });

  if (!isAdmin) return null;

  return (
    <Card className="p-4 flex flex-col gap-3 max-w-[640px]">
      <h2 className="text-sm font-semibold m-0">Order webhook</h2>
      <p className="m-0 text-sm text-muted">
        The shop sends every new or changed order to NP-Flow. In WooCommerce → Settings → Advanced →
        Webhooks, add one webhook per topic (Order created, Order updated) with API version WP REST
        API Integration v3, the delivery URL below and the same secret you save here.
      </p>

      {isLoading ? <div className="text-muted text-sm">Loading status…</div> : null}
      {isError ? <Alert>Status could not be loaded.</Alert> : null}

      {data ? (
        <div className="flex flex-col gap-1 text-sm">
          <div>
            Status:{' '}
            <span className={data.configured ? 'text-success-text' : 'text-muted'}>
              {data.configured ? 'Secret saved' : 'No secret saved'}
            </span>
          </div>
          {data.configured && data.lastRotatedAt !== null ? (
            <div className="text-xs text-muted">Last set {formatDateTime(data.lastRotatedAt)}</div>
          ) : null}
          {data.deliveryUrl ? (
            <div className="flex flex-wrap items-center gap-2">
              <span>Delivery URL:</span>
              <span className="font-mono break-all">{data.deliveryUrl}</span>
              <Button
                variant="secondary"
                onClick={() => {
                  const url = data.deliveryUrl;
                  if (!url) return;
                  navigator.clipboard
                    ?.writeText(url)
                    .then(() => setCopied(true))
                    .catch(() => setCopied(false));
                }}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          ) : (
            <Alert>This account has no public domain yet, so there is no delivery URL.</Alert>
          )}
        </div>
      ) : null}

      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="secondary" onClick={() => setSheetOpen(true)}>
          {data?.configured ? 'Replace secret' : 'Set secret'}
        </Button>
      </div>

      <SetWebhookSecretSheet open={sheetOpen} onOpenChange={setSheetOpen} />
    </Card>
  );
}

function setOrderSignupErrorText(err: unknown): string {
  if (isTotpLocked(err)) return TOTP_LOCKED_MESSAGE;
  if (isStepUpRequired(err)) return STEP_UP_ENDED_MESSAGE;
  if (err instanceof ApiError && err.status === 401)
    return 'The code is incorrect or has already been used.';
  return 'The setting could not be saved. Try again.';
}

function SetOrderSignupSheet({
  open,
  onOpenChange,
  enable,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** What the sheet switches to. */
  enable: boolean;
}) {
  const [totpCode, setTotpCode] = useState('');
  const queryClient = useQueryClient();
  const stepUp = useStepUpWindow();
  const codeId = useId();

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch<WooOrderSignupStatus>('/settings/woo/order-signup', {
        method: 'POST',
        body: { enabled: enable, ...(stepUp.active ? {} : stepUpBody(totpCode)) },
      }),
    onError: stepUp.onStepUpError,
    onSuccess: () => {
      if (!stepUp.active) stepUp.onStepUpSuccess();
      onOpenChange(false);
      setTotpCode('');
      void queryClient.invalidateQueries({ queryKey: ['woo-order-signup'] });
    },
  });

  const canSubmit = (stepUp.active || stepUpCodeReady(totpCode)) && !mutation.isPending;

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) mutation.reset();
      }}
    >
      {open ? (
        <SheetContent
          eyebrow="WooCommerce"
          title={
            enable ? 'Turn on: buyers join the newsletter' : 'Turn off: buyers join the newsletter'
          }
          description={
            enable
              ? 'From now on, every new paid order puts its buyer on the newsletter. Orders placed while it was off are not added afterwards. Requires a code from the authenticator app (not if you entered one in the last 30 minutes).'
              : 'New orders stop adding buyers to the newsletter. Buyers already added stay on it. Requires a code from the authenticator app (not if you entered one in the last 30 minutes).'
          }
        >
          <form
            id="woo-order-signup-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) mutation.mutate();
            }}
          >
            <SheetBody>
              {mutation.isError ? <Alert>{setOrderSignupErrorText(mutation.error)}</Alert> : null}
              <StepUpCodeField
                id={codeId}
                value={totpCode}
                onChange={setTotpCode}
                active={stepUp.active}
                minutesLeft={stepUp.minutesLeft}
              />
            </SheetBody>
            <SheetFooter>
              <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={!canSubmit}>
                {mutation.isPending ? 'Saving…' : enable ? 'Turn on' : 'Turn off'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      ) : null}
    </Sheet>
  );
}

/**
 * Buyers join the newsletter when they order (`order_signup_settings`,
 * `apps/edge/src/lib/orderSignup.ts`, docs/decisions.md "Købere kommer på
 * nyhedsbrevet ved køb"). Admin only; switching needs a fresh code.
 */
export function WooOrderSignupSection() {
  const { isAdmin } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['woo-order-signup'],
    queryFn: () => apiFetch<WooOrderSignupStatus>('/settings/woo/order-signup'),
    enabled: isAdmin,
  });

  if (!isAdmin) return null;

  return (
    <Card className="p-4 flex flex-col gap-3 max-w-[640px]">
      <h2 className="text-sm font-semibold m-0">Buyers join the newsletter</h2>
      <p className="m-0 text-sm text-muted">
        Every new paid order (processing, on hold or completed) puts the buyer on the newsletter,
        with no confirmation mail. Someone who has unsubscribed, bounced or complained is never
        added again. Orders from before it was switched on are not added.
      </p>

      {isLoading ? <div className="text-muted text-sm">Loading status…</div> : null}
      {isError ? <Alert>Status could not be loaded.</Alert> : null}

      {data ? (
        <div className="flex flex-col gap-1 text-sm">
          <div>
            Status:{' '}
            <span className={data.enabled ? 'text-success-text' : 'text-muted'}>
              {data.enabled ? 'On' : 'Off'}
            </span>
          </div>
          {data.enabled && data.enabledAt !== null ? (
            <div className="text-xs text-muted">
              Orders from {formatDateTime(data.enabledAt)} onwards
            </div>
          ) : null}
        </div>
      ) : null}

      {data ? (
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={() => setSheetOpen(true)}>
            {data.enabled ? 'Turn off' : 'Turn on'}
          </Button>
        </div>
      ) : null}

      {data ? (
        <SetOrderSignupSheet open={sheetOpen} onOpenChange={setSheetOpen} enable={!data.enabled} />
      ) : null}
    </Card>
  );
}

export function WooPage() {
  return (
    <>
      <header className="flex items-center gap-3">
        <h1 className="m-0 text-2xl font-semibold tracking-tight">WooCommerce</h1>
      </header>
      <WooSection />
      <WooWebhookSection />
      <WooOrderSignupSection />
    </>
  );
}
